import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Parse the filename from a `Content-Disposition` header, preferring
 * the RFC 5987 `filename*=UTF-8''…` variant (proper Unicode support)
 * over the legacy ASCII `filename="…"` form. Returns null when neither
 * variant is present.
 */
function parseContentDispositionFilename(disp) {
  if (!disp) return null;
  // Try RFC 5987 first — `filename*=UTF-8''<percent-encoded>`
  const star = /filename\*\s*=\s*([^']*)''([^;\s]+)/i.exec(disp);
  if (star?.[2]) {
    try { return decodeURIComponent(star[2]); }
    catch { /* fall through to legacy form */ }
  }
  const legacy = /filename\s*=\s*"?([^";]+)"?/i.exec(disp);
  return legacy?.[1] || null;
}

/**
 * Trigger a CSV download from a protected API endpoint.
 *
 * The backend sets `Content-Disposition: attachment; filename=…`, but the
 * browser cannot honour that header automatically when we fetch with an
 * `Authorization` bearer (the response is a JS Blob, not a top-level
 * navigation). So we recreate the download flow manually:
 *   1. fetch as a Blob (verify the response is non-empty + a real Blob)
 *   2. parse the filename from the response header (RFC 5987 → legacy)
 *   3. create a hidden <a download> and click it
 *
 * Shows a Norwegian toast on success / failure using the existing app style.
 * The success toast is only fired *after* the click has been dispatched,
 * so the user never sees "✓ eksportert" when the download silently failed.
 *
 * @param {string} path     API path (e.g. "/export/customers.csv")
 * @param {string} fallback Default filename if header is missing
 * @param {string} label    User-friendly noun for toasts ("kunder", "bookinger", "forespørsler")
 */
export async function downloadCsv(path, fallback, label) {
  const t = toast.loading(`Eksporterer ${label}…`);
  try {
    const res = await api.get(path, { responseType: "blob" });
    // Sanity-check the response is an actual non-empty Blob. Without
    // this, a misconfigured endpoint could return an empty body and we'd
    // still flash a "success" toast.
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data || ""], { type: "text/csv;charset=utf-8" });
    if (!blob.size) {
      toast.error(`Eksportfilen var tom`, { id: t });
      return;
    }
    const filename = parseContentDispositionFilename(res.headers?.["content-disposition"]) || fallback;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after the click is processed so older browsers still serve the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} eksportert (${filename})`, { id: t });
  } catch (err) {
    if (err?.response?.status === 403) {
      toast.error("Du har ikke tilgang til denne eksporten.", { id: t });
    } else if (err?.response?.status === 402) {
      toast.error("Premium-funksjon. Oppgrader for å bruke denne eksporten.", { id: t });
    } else {
      toast.error(`Kunne ikke eksportere ${label}`, { id: t });
    }
  }
}

/** Same as downloadCsv but with a generic "Laster ned …" label and no
 *  CSV-specific toast wording. Used for PDF/binary downloads. */
export async function downloadFile(path, fallback, label) {
  const t = toast.loading(`Laster ned ${label}…`);
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data || ""]);
    if (!blob.size) {
      toast.error(`Filen var tom`, { id: t });
      return;
    }
    const filename = parseContentDispositionFilename(res.headers?.["content-disposition"]) || fallback;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.rel = "noopener"; a.style.display = "none";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} lastet ned`, { id: t });
  } catch (err) {
    if (err?.response?.status === 402) {
      toast.error("Premium-funksjon. Oppgrader for å bruke denne nedlastingen.", { id: t });
    } else {
      toast.error(`Kunne ikke laste ned ${label}`, { id: t });
    }
  }
}
